import { DataTable, Icon } from '@hai/ui-react';
import { HaiSortDirection } from '@hai/ui-react/dist/types';
import moment from 'moment';
import React, { useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useStores } from 'src/js/hook/use-stores';
import { constant } from 'src/js/constant';
import { useLocalStorage } from 'usehooks-ts';
import { ListSelector } from '../../util/listSelector';
import { presetName, sortDataTable } from './preset-helper';
import { Presets, PresetViewModel } from './preset-models';
import { useStateWatch } from 'src/js/store/use-watch';

interface Props {
  listController: ListSelector<PresetViewModel>;
  presets: Presets;
  setSelected: (selected: boolean) => void;
}

export const renderActiveIcon = (
  t: TFunction,
  name: string,
  active: string,
  autoSave: boolean,
  modified: boolean,
  handleSelect: (name: string, e: any) => void,
) => {
  if (name === active) {
    if (autoSave) {
      // NOTE: Span is needed for title tooltip because the noPointerEvent on Icon prevents tooltips
      return (
        <span
          className="mt-1"
          title={t('presets.presetSaved')}
          onClick={(e: any) => handleSelect && handleSelect(name, e)}
        >
          <Icon className="noPointerEvent" iconname="StatusOK" />
        </span>
      );
    } else {
      if (!modified) {
        return (
          <span
            className="mt-1"
            title={t('presets.presetSaved')}
            onClick={(e: any) => handleSelect && handleSelect(name, e)}
          >
            <Icon className="noPointerEvent" iconname="StatusOK" />
          </span>
        );
      } else {
        return (
          <span
            className="mt-1"
            title={t('presets.presetNotSaved')}
            onClick={(e: any) => handleSelect && handleSelect(name, e)}
          >
            <Icon className="noPointerEvent" iconname="StatusWarning" />
          </span>
        );
      }
    }
  }
  return undefined;
};

export const PresetLoadTable: React.FunctionComponent<Props> = ({ listController, presets, setSelected }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const [fieldKey, setFieldKey] = useLocalStorage(
    `${constant.lStorage.presetLoadTable}:fieldKey:${sessionStore.username}`,
    'name',
  );
  const [sortFieldKey, setSortFieldKey] = useLocalStorage(
    `${constant.lStorage.presetLoadTable}:sortFieldKey:${sessionStore.username}`,
    'name',
  );
  const [direction, setDirection] = useLocalStorage<HaiSortDirection>(
    `${constant.lStorage.presetLoadTable}:directionKey:${sessionStore.username}`,
    HaiSortDirection.ASC,
  );

  const onChangeSort = (fieldKey: string, order: HaiSortDirection, sortFieldKey: string) => {
    setFieldKey(fieldKey);
    setDirection(order);
    setSortFieldKey(sortFieldKey);
  };

  const handleSelect = (name: string, _e: any) => {
    // TODO simplify this logic
    // Enforce Single Selection
    // console.log('handleSelect', name, e, listController.selectedRows());
    if (listController.selectedRows().length > 0) {
      if (listController.isSelected(name)) {
        listController.toggleRow(name);
      } else {
        listController.unselectAll();
        listController.select(name);
      }
    } else {
      listController.select(name);
    }
    setSelected(listController.isSelected(name));
  };

  // Add title to presets that are too long
  useEffect(() => {
    const timeout = setTimeout(() => {
      document.querySelectorAll('.preset-table .text-data').forEach((e: any) => {
        if (e.offsetWidth < e.scrollWidth) {
          e.setAttribute('title', e.textContent);
        }
      });
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const selectedMatrix = useStateWatch(listController, 'rowMatrix', listController.rowMatrix);

  return (
    <div className="preset-table load scrollable">
      <DataTable
        selectable={true}
        sortable={true}
        allowSortNone={false}
        initialSort={{ fieldKey: fieldKey, order: direction, sortFieldKey: sortFieldKey }}
        onSort={onChangeSort}
        sortFunction={sortDataTable}
        columnStructure={[
          { fieldKey: 'name', useFsMask: true, title: t('general.name') },
          {
            fieldKey: 'activeJSX',
            useFsMask: true,
            title: t('presets.active'),
            alignment: 'center',
            sortFieldKey: 'active',
            className: 'presetLoadTableActiveColumn',
          },
          { fieldKey: 'modifiedTime', title: t('presets.modifiedTime'), sortFieldKey: 'mtime' },
        ]}
        className="datatable-modal"
        singleSelect
        emptyStateText={t('presets.addFirst')}
      >
        <DataTable.Header />
        {listController.list?.map((value: PresetViewModel, index: number) => (
          <DataTable.Row
            key={`${value.name}`}
            {...({ id: value.name } as any)} // FIXME better typing needed in HaiUI
            rowData={{
              activeJSX: renderActiveIcon(
                t,
                value.name,
                presets.active,
                presets.autosave,
                presets.activeWasModified,
                handleSelect,
              ),
              active: value.name === presets.active,
              index: index,
              id: `${value.name}`,
              modifiedTime: moment.unix(Number(value.mtime)).format(constant.momentFormat.presetDateTime),
              name: presetName(value.name),
              mtime: value.mtime,
            }}
            checked={selectedMatrix[index]}
            //  onClick={(e:any) => handleSelect(value.name, e)}
            onSelect={(e: any) => handleSelect(value.name, e)}
          ></DataTable.Row>
        ))}
      </DataTable>
    </div>
  );
};
